import { faArrowUp, faArrowDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@pickleballinc/ui/lib/utils';

interface ChangeIndicatorData {
	changePercentage: number;
	isPositive: boolean;
}

interface StandingPointsData {
	value: number;
}

interface MatchupsData {
	wins: { reg: number; tb: number; tot: number };
	losses: { reg: number; tb: number; tot: number };
}

interface SimpleStatsData {
	headers: string[];
	values: (string | number)[];
}

interface ColumnData {
	header: string;
	changeIndicator?: ChangeIndicatorData;
	type: 'standing-points' | 'matchups' | 'simple-stats';
	width?: string;
	data: StandingPointsData | MatchupsData | SimpleStatsData;
}

interface TeamStatsCardProps {
	columns: ColumnData[];
	className?: string;
}

const ChangeIndicator = ({ changePercentage, isPositive }: ChangeIndicatorData) => (
	<div
		className={cn('inline-flex items-center gap-1', {
			'text-green-600': isPositive,
			'text-red-600': !isPositive
		})}
	>
		<FontAwesomeIcon
			icon={isPositive ? faArrowUp : faArrowDown}
			className={cn('h-3 w-3', {
				'text-green-600': isPositive,
				'text-[#B42318]': !isPositive
			})}
		/>
		<span
			className={cn('text-sm', {
				'text-green-600': isPositive,
				'text-[#B42318]': !isPositive
			})}
		>
			{Math.abs(changePercentage).toFixed(2)}%
		</span>
	</div>
);

const StandingPointsCell = ({ data }: { data: StandingPointsData }) => (
	<div className="p-6 flex items-center justify-center">
		<div className="text-4xl font-bold text-gray-600 text-center">{data.value}</div>
	</div>
);

const MatchupsCell = ({ data }: { data: MatchupsData }) => (
	<div className="p-6 flex items-center justify-center">
		<div className="flex gap-4 justify-center">
			<div className="text-center flex-shrink-0">
				<div className="text-sm font-medium text-gray-900 text-center">Wins</div>
				<hr className="border-gray-400 mb-1" />
				<div className="flex gap-2 text-xs text-gray-600 mb-2 justify-center">
					<div className="w-8 text-center">Reg</div>
					<div className="w-8 text-center">TB</div>
					<div className="w-8 text-center">Tot</div>
				</div>
				<div className="flex gap-2 text-sm text-gray-600 font-bold justify-center">
					<div className="w-8 text-center">{data.wins.reg}</div>
					<div className="w-8 text-center">{data.wins.tb}</div>
					<div className="w-8 text-center">{data.wins.tot}</div>
				</div>
			</div>
			<div className="text-center flex-shrink-0">
				<div className="text-sm font-medium text-gray-900 text-center">Losses</div>
				<hr className="border-gray-400 mb-1" />
				<div className="flex gap-2 text-xs text-gray-600 mb-2 justify-center">
					<div className="w-8 text-center">Reg</div>
					<div className="w-8 text-center">TB</div>
					<div className="w-8 text-center">Tot</div>
				</div>
				<div className="flex gap-2 text-sm text-gray-600 font-bold justify-center">
					<div className="w-8 text-center">{data.losses.reg}</div>
					<div className="w-8 text-center">{data.losses.tb}</div>
					<div className="w-8 text-center">{data.losses.tot}</div>
				</div>
			</div>
		</div>
	</div>
);

const SimpleStatsCell = ({ data }: { data: SimpleStatsData }) => {
	const getWidthClass = (index: number, total: number) => {
		if (total === 2) return 'w-12';
		if (total === 3) return index === 2 ? 'w-12' : 'w-8';
		if (total === 4) return index < 2 ? 'w-7' : 'w-8';
		return 'w-8';
	};

	const getGapClass = (total: number) => {
		if (total === 4) return 'gap-6';
		if (total === 3) return 'gap-2';
		return 'gap-4';
	};

	return (
		<div className="p-6 flex items-center justify-center">
			<div className="space-y-2">
				<div className={cn('flex text-xs text-gray-600 justify-center', getGapClass(data.headers.length))}>
					{data.headers.map((header, index) => (
						<div key={index} className={cn('text-center', getWidthClass(index, data.headers.length))}>
							{header}
						</div>
					))}
				</div>
				<div className={cn('flex text-sm text-gray-600 font-bold justify-center', getGapClass(data.values.length))}>
					{data.values.map((value, index) => (
						<div key={index} className={cn('text-center', getWidthClass(index, data.values.length))}>
							{value}
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

export const TeamStatsCard = ({ columns, className = '' }: TeamStatsCardProps) => {
	const gridTemplateColumns = columns.map((col) => col.width || '1fr').join(' ');

	const renderCell = (column: ColumnData, index: number) => {
		const isLastColumn = index === columns.length - 1;
		const baseClasses = cn('flex items-center justify-center', {
			'border-r border-gray-200': !isLastColumn
		});

		switch (column.type) {
			case 'standing-points':
				return (
					<div key={index} className={baseClasses}>
						<StandingPointsCell data={column.data as StandingPointsData} />
					</div>
				);
			case 'matchups':
				return (
					<div key={index} className={baseClasses}>
						<MatchupsCell data={column.data as MatchupsData} />
					</div>
				);
			case 'simple-stats':
				return (
					<div key={index} className={baseClasses}>
						<SimpleStatsCell data={column.data as SimpleStatsData} />
					</div>
				);
			default:
				return null;
		}
	};

	return (
		<div className={cn('w-full overflow-x-auto rounded-lg border-l border-r border-b border-gray-300 shadow-md', className)}>
			<div className="bg-white rounded-lg border border-gray-200 overflow-hidden w-max min-w-full">
				<div className="grid bg-gray-200" style={{ gridTemplateColumns }}>
					{columns.map((column, index) => {
						const isLastColumn = index === columns.length - 1;
						return (
							<div
								key={index}
								className={cn('p-4 flex items-center justify-center', {
									'border-r border-gray-300': !isLastColumn
								})}
							>
								<div className="flex items-center justify-center gap-2">
									<h3 className="text-base font-bold text-gray-900 text-center">{column.header}</h3>
									{column.changeIndicator && (
										<ChangeIndicator
											changePercentage={column.changeIndicator.changePercentage}
											isPositive={column.changeIndicator.isPositive}
										/>
									)}
								</div>
							</div>
						);
					})}
				</div>

				<div className="grid" style={{ gridTemplateColumns }}>
					{columns.map((column, index) => renderCell(column, index))}
				</div>
			</div>
		</div>
	);
};
